import {
  Button,
  CheckInput,
  Icon,
  Modal,
  Select,
  SelectOptionType,
  TextAreaInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { AcademicOfferRequirement } from '../types';

export type AcademicOfferRequirementsBaseModalMethods = {
  open: (record?: AcademicOfferRequirement) => void;
  close: () => void;
};

type AcademicOfferRequirementsBaseModalProps = {
  admissionRequirements?: SelectOptionType[];
  onSave?: (updated: AcademicOfferRequirement) => void;
};

type FormFields = {
  admissionRequirement: SelectOptionType | null;
  description: string;
  requiresFileUpload: boolean;
};

const DEFAULT_VALUES: FormFields = {
  admissionRequirement: null,
  description: '',
  requiresFileUpload: false,
};

const AcademicOfferRequirementsBaseModal = (
  {
    admissionRequirements = [],
    onSave,
  }: AcademicOfferRequirementsBaseModalProps,
  ref: Ref<AcademicOfferRequirementsBaseModalMethods>,
) => {
  const { msgValidations } = useValidations();
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const [visible, setVisible] = useState(false);
  const [, setRecord] = useState<AcademicOfferRequirement>();

  const { control, handleSubmit, reset, watch } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const [descriptionValue] = watch(['description']);

  const descriptionCount = useMemo(() => {
    return descriptionValue?.length ?? 0;
  }, [descriptionValue]);

  const handleOpen = useCallback(
    (nextRecord?: AcademicOfferRequirement) => {
      if (nextRecord) {
        reset({
          admissionRequirement:
            admissionRequirements.find(
              (option) => option.value === nextRecord.admissionRequirementId,
            ) || null,
          requiresFileUpload: nextRecord.requiresFileUpload,
          description: nextRecord.description ?? '',
        });
      } else {
        reset(DEFAULT_VALUES);
      }
      setVisible(true);
      setRecord(nextRecord);
    },
    [reset, admissionRequirements],
  );

  const handleClose = useCallback(() => {
    setVisible(false);
    setRecord(undefined);
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(
    (data: FormFields) => {
      !!onSave &&
        onSave({
          admissionRequirementId: data.admissionRequirement?.value as number,
          description: data.description,
          requiresFileUpload: data.requiresFileUpload,
        });
      setVisible(false);
      setRecord(undefined);
    },
    [onSave],
  );

  return (
    <Modal isOpen={!!visible} size="lg" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12} className="text-center">
            <Icon name="information" size={52} className="mx-auto" />
            <h2 className="text-dark fs-22 mb-0 w-100 text-center mt-4">
              {t(`requirements.base.modal.title`)}
            </h2>
            <p className="fs-16 w-100 text-center mt-2">
              {t(`requirements.base.modal.description`)}
            </p>
          </Col>
        </Row>
        <Row className="my-5">
          <Col xs={12} md={12}>
            <Select
              control={control}
              name="admissionRequirement"
              options={admissionRequirements}
              label={t(`requirements.base.modal.admissionRequirementLabel`)}
              isClearable={false}
              rules={{ required: msgValidations.required }}
            />
          </Col>
          <Col xs={12} md={12}>
            <CheckInput
              control={control}
              name="requiresFileUpload"
              label={t(`requirements.base.modal.requiresFileUploadLabel`)}
            />
          </Col>
          <Col xs={12} md={12}>
            <TextAreaInput
              control={control}
              name="description"
              label={t(`requirements.base.modal.descriptionLabel`)}
              rules={{
                required: false,
              }}
              maxLength={150}
            />
            <p>{`${descriptionCount} / 150`}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.cancel')}
              type="button"
              fullwidth
              outlined
              onClick={handleClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.add')}
              type="button"
              fullwidth
              onClick={handleSubmit(handleSave)}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(AcademicOfferRequirementsBaseModal);
